import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { ENTITIES, SERVICES } from "@/data/content";

const useClock = (timeZone) => {
  const [now, setNow] = useState("");
  useEffect(() => {
    const tick = () =>
      setNow(
        new Intl.DateTimeFormat("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone,
        }).format(new Date())
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timeZone]);
  return now;
};

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/industries", label: "Industries" },
  { to: "/careers", label: "Careers" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

function EntityClock({ timeZone }) {
  const time = useClock(timeZone);
  return (
    <span className="inline-flex items-center gap-2 font-mono text-xs text-volt">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-volt opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-volt" />
      </span>
      {time}
    </span>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#060609]">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <Link to="/contact" data-testid="footer-wordmark" className="group block">
          <span className="font-display text-[16vw] font-black uppercase leading-none tracking-tight text-white/10 transition-colors duration-700 group-hover:text-brand/40 lg:text-[10rem]">
            Vigarbiz
          </span>
        </Link>

        <div className="mt-12 grid gap-12 lg:grid-cols-[1.2fr_1fr_1fr]">
          <div>
            <p className="max-w-sm text-base leading-relaxed text-muted2">
              A full-service digital marketing agency engineered across two continents — strategy in
              Fremont, delivery at scale in Pune. Search, paid, social, content, web and analytics
              under one roof.
            </p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-dim">
              Pune × Fremont
            </p>
          </div>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Explore</p>
            <ul className="mt-5 space-y-3">
              {NAV.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-sm text-muted2 transition-colors hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Services</p>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <Link
                    to="/services"
                    data-testid={`footer-service-${s.id}`}
                    className="group inline-flex items-center gap-1 text-sm text-muted2 transition-colors hover:text-white"
                  >
                    {s.title}
                    <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {ENTITIES.map((e) => (
            <div
              key={e.key}
              data-testid={`footer-entity-${e.key}`}
              className="glow-card rounded-2xl border border-white/10 bg-surface p-6 sm:p-8"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <img src="/logo.png" alt="Vigarbiz logo" className="h-9 w-auto" />
                  <h3 className="font-display text-xl font-bold text-white">{e.name}</h3>
                </div>
                <EntityClock timeZone={e.timeZone} />
              </div>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-dim">{e.region}</p>
              <div className="mt-4 space-y-2 text-sm text-muted2">
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  {e.address}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 shrink-0 text-brand" />
                  <a href={`mailto:${e.email}`} className="transition-colors hover:text-white">
                    {e.email}
                  </a>
                </p>
                <p className="flex items-start gap-2">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>
                    {e.phones.map((p, i) => (
                      <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="transition-colors hover:text-white">
                        {p}
                        {i < e.phones.length - 1 ? " · " : ""}
                      </a>
                    ))}
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-dim">
            © 2026 Vigarbiz ITES Pvt Ltd · Vigarbiz Inc. All rights reserved.
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-dim">
            Registered in India & the United States
          </p>
        </div>
      </div>
    </footer>
  );
}
